import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { FiPlusCircle, FiEdit2, FiTrash2, FiBookOpen } from "react-icons/fi";
import courseService from "../../../services/courseService";

export default function CourseList() {
    const navigate = useNavigate();
    const [courses, setCourses] = useState([]);
    const [loading, setLoading] = useState(true);
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [deleting, setDeleting] = useState(false);

    const fetchCourses = async () => {
        setLoading(true);
        try {
            const res = await courseService.getAll();
            setCourses(res.data || []);
        } catch (err) {
            toast.error(err.message);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchCourses();
    }, []);

    const toggleActive = async (course) => {
        try {
            await courseService.update(course._id, { isActive: !course.isActive });
            toast.success(course.isActive ? "Course marked inactive." : "Course marked active.");
            fetchCourses();
        } catch (err) {
            toast.error(err.response?.data?.message || err.message);
        }
    };

    const confirmDelete = async () => {
        if (!deleteTarget) return;
        setDeleting(true);
        try {
            await courseService.remove(deleteTarget._id);
            toast.success("Course deleted.");
            setDeleteTarget(null);
            fetchCourses();
        } catch (err) {
            toast.error(err.response?.data?.message || err.message);
        } finally {
            setDeleting(false);
        }
    };

    return (
        <div>
            <div className="page-header">
                <div>
                    <h1 className="page-title">Courses</h1>
                    <p className="page-subtitle">
                        Define the courses your college offers — used across Students, Fee Structures, and (soon) Subjects, Timetables, and Exams.
                    </p>
                </div>
                <button className="btn btn-primary" onClick={() => navigate("/settings/courses/add")}>
                    <FiPlusCircle size={14} style={{ marginRight: 6 }} />
                    Add Course
                </button>
            </div>

            <div className="card">
                <div className="card-body" style={{ padding: 0 }}>
                    {loading ? (
                        <div className="loading-center">
                            <div className="spinner" />
                            Loading courses...
                        </div>
                    ) : courses.length === 0 ? (
                        <div className="empty-state">
                            <FiBookOpen />
                            <p>No courses defined yet. Add one to get started.</p>
                        </div>
                    ) : (
                        <div className="table-wrapper">
                            <table>
                                <thead>
                                    <tr>
                                        <th>Course</th>
                                        <th>Code</th>
                                        <th>Duration</th>
                                        <th>Semesters/Year</th>
                                        <th>Status</th>
                                        <th></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {courses.map((c) => (
                                        <tr key={c._id}>
                                            <td>
                                                <div style={{ fontWeight: 600 }}>{c.shortName}</div>
                                                <div style={{ fontSize: 11.5, color: "var(--text-muted)" }}>{c.courseName}</div>
                                            </td>
                                            <td>{c.courseCode}</td>
                                            <td>
                                                {c.durationYears} year{c.durationYears > 1 ? "s" : ""}
                                            </td>
                                            <td>{c.semestersPerYear}</td>
                                            <td>
                                                <button
                                                    type="button"
                                                    className={`badge ${c.isActive ? "badge-green" : "badge-blue"}`}
                                                    style={{ border: "none", cursor: "pointer" }}
                                                    onClick={() => toggleActive(c)}
                                                    title="Click to toggle"
                                                >
                                                    {c.isActive ? "Active" : "Inactive"}
                                                </button>
                                            </td>
                                            <td>
                                                <div style={{ display: "flex", gap: "0.4rem", justifyContent: "flex-end" }}>
                                                    <button
                                                        className="btn btn-outline btn-sm"
                                                        title="Edit"
                                                        onClick={() => navigate(`/settings/courses/${c._id}/edit`)}
                                                    >
                                                        <FiEdit2 size={13} />
                                                    </button>
                                                    <button className="btn btn-outline btn-sm" title="Delete" onClick={() => setDeleteTarget(c)}>
                                                        <FiTrash2 size={13} />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </div>

            {deleteTarget && (
                <div className="modal-overlay" onClick={() => !deleting && setDeleteTarget(null)}>
                    <div className="modal" onClick={(e) => e.stopPropagation()}>
                        <h3>Delete Course</h3>
                        <p>
                            Delete <strong>{deleteTarget.shortName}</strong>? This will fail if any students are already linked to it — mark it
                            inactive instead if you just want to stop new admissions under it.
                        </p>
                        <div className="modal-actions">
                            <button className="btn btn-outline" onClick={() => setDeleteTarget(null)} disabled={deleting}>
                                Cancel
                            </button>
                            <button className="btn btn-danger" onClick={confirmDelete} disabled={deleting}>
                                {deleting ? "Deleting..." : "Delete"}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}